import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";

const CATEGORIES = ["All", "Stress reset", "Focus and clarity", "Pre-performance", "Recovery", "Sleep and downregulation", "Anxiety support"];

export default function Library() {
  const navigate = useNavigate();
  const [practices, setPractices] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/practices").then((r) => setPractices(r.data));
  }, []);

  const filtered = useMemo(() => {
    return practices.filter((p) => {
      const matchesCat = active === "All" || p.category === active;
      const matchesQ = !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.purpose.toLowerCase().includes(q.toLowerCase());
      return matchesCat && matchesQ;
    });
  }, [practices, active, q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12 lg:py-16">
      <p className="overline text-primary mb-2">Practice library</p>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">Guided breathwork</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">Find the right practice for the state you're in — or the state you need next.</p>

      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`category-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
                active === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:border-primary/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search practices..." className="pl-9 h-10 rounded-full" data-testid="library-search-input" />
        </div>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card className="p-7 h-full hover:border-primary/40 transition-colors cursor-pointer" onClick={() => navigate(`/practice/${p.id}`)} data-testid={`library-card-${p.id}`}>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="secondary" className="text-xs">{p.category}</Badge>
                <span className="text-xs text-muted-foreground">{p.duration_minutes} min · {p.difficulty}</span>
              </div>
              <h3 className="font-display text-xl font-semibold tracking-tight">{p.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.purpose}</p>
              <Button variant="ghost" size="sm" className="mt-5 -ml-3 text-primary">Start practice <ArrowRight className="ml-1 h-3 w-3" /></Button>
            </Card>
          </motion.div>
        ))}
        {filtered.length === 0 && (
          <Card className="p-10 col-span-full text-center text-muted-foreground">No practices match your filter.</Card>
        )}
      </div>
    </div>
  );
}
